import { useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { MAP_FOCUS_ZOOM } from '../map/config/mapConfig';
import { TransportStopListItem } from '../transport/TransportStopListItem';
import { fmtDist, stopTypeLabel, type TransportStopFeature } from '../transport/transportStopUtils';
import { useNearbyTransportStops } from '../transport/useNearbyTransportStops';

interface NearbyTransportStopsSectionProps {
  onNavigateToStop?: (name: string, coords: { lat: number; lng: number }) => void;
  onStopSelect?: () => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

export function NearbyTransportStopsSection({
  onNavigateToStop,
  onStopSelect,
  scrollRef,
}: NearbyTransportStopsSectionProps) {
  const { flyToLocation, setSheetState, setSelectedStopCoords } = useMapStore();
  const { anchor, loading, error, sortedStops, reload } = useNearbyTransportStops();
  const sectionRef = useRef<HTMLDivElement>(null);
  const ref = scrollRef ?? sectionRef;

  const handleStopClick = (stop: TransportStopFeature) => {
    const [lng, lat] = stop.geometry.coordinates;
    const name = stop.properties?.name || stopTypeLabel(stop.properties?.type);
    setSelectedStopCoords({ lat, lng, name });
    flyToLocation({ lat, lng }, MAP_FOCUS_ZOOM);
    setSheetState('collapsed');
    onStopSelect?.();
  };

  const handleNavigate = (stop: TransportStopFeature) => {
    const [lng, lat] = stop.geometry.coordinates;
    const name = stop.properties?.name || stopTypeLabel(stop.properties?.type);
    onNavigateToStop?.(name, { lat, lng });
  };

  return (
    <div ref={ref} id="nearby-transport-stops" className="p-4 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Nearby transport stops
        </p>
        {!loading && (
          <button
            type="button"
            onClick={() => reload()}
            className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            Refresh
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-6 text-gray-500 dark:text-gray-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Finding stops…
        </div>
      )}

      {!loading && error && (
        <p className="text-xs text-red-500 dark:text-red-400 py-2 text-center">{error}</p>
      )}

      {!loading && !error && !anchor && (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
          Turn on location to see the nearest trotro, bus, taxi, and rail stops.
        </p>
      )}

      {!loading && !error && anchor && sortedStops.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-2 text-center">
          No verified stops within {fmtDist(1000)}.
        </p>
      )}

      {!loading && !error && sortedStops.length > 0 && (
        <div className="space-y-1.5">
          {sortedStops.slice(0, 8).map((stop) => (
            <TransportStopListItem
              key={String(stop.properties?.id ?? stop.id)}
              stop={stop}
              anchor={anchor}
              onSelect={handleStopClick}
              onNavigate={onNavigateToStop ? handleNavigate : undefined}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}
